import { useCallback, useEffect, useMemo, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import moment from "moment"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { Button, Icon, Label, Loader, Modal, Popup, Tab } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { ImageWithFallback } from "simplydo/react/components/ImageWithFallback"

import api, { asQuery } from "api"
import ConfigurableTable from "components/lib/ConfigurableTable"
import util from "utils/utils"

type UserAssessmentsModalProps = {
  user: OpenAPI.GET<"/users/me">["response"]
  assessorId: string
  onClose: () => void
  challenge: OpenAPI.Schemas["Challenge"] & OpenAPI.Schemas["AssignChallengeAssessmentDetail"]
  unsubmitAssessment: (ideaId: string, assessorId: string, assessmentRoundId: string) => void
  unassignAssessor: (ideaId: string, assessorId: string, assessmentRoundId: string) => void
  assessmentRoundId?: string
}

const UserAssessmentsModal = ({
  user,
  assessorId,
  onClose,
  challenge,
  unsubmitAssessment,
  unassignAssessor,
  assessmentRoundId,
}: UserAssessmentsModalProps) => {
  const { t } = useTranslation()

  const template = challenge?.ownerIdeaTemplate
  const assessorQuery = useQuery({
    queryKey: ["challenges", challenge._id, "assessments", "dashboard", assessorId],
    queryFn: asQuery(api.challenges.getAssessor, {
      params: [challenge._id, assessorId],
    }),
    enabled: !!user,
  })

  const assessmentRounds = useMemo(() => {
    const availableRounds = []
    assessorQuery.data?.ideas?.forEach((idea) => {
      idea.assessments?.forEach((assessment) => {
        if (assessment.user === assessorId) {
          const roundId = assessment.assessmentRoundId || "legacy"
          if (!availableRounds.includes(roundId)) {
            availableRounds.push(roundId)
          }
        }
      })
    })

    const allRounds = template?.assessments?.rounds || []
    return allRounds.filter((round) => availableRounds.includes(round.id))
  }, [assessorQuery.data?.ideas, template, assessorId])

  const [selectedAssessmentRoundId, setSelectedAssessmentRoundId] = useState<string | null>(assessmentRoundId)

  const ideaTemplate = challenge.ownerIdeaTemplate

  const remindAssessor = useCallback(
    (assessorId, assessmentRoundId) => {
      api.challenges.remindAssessor(
        challenge?._id,
        assessorId,
        assessmentRoundId,
        () => {
          toast.success("Reminder sent to assessor")
          api.queryClient.invalidateQueries({
            queryKey: ["challenges", challenge._id, "assessments", "dashboard", assessorId],
          })
        },
        () => {
          toast.error("Failed to send reminder")
        },
      )
    },
    [challenge?._id],
  )

  if (!ideaTemplate) return null
  if (assessorQuery?.isLoading) return null

  return (
    <Modal mountNode={document.getElementById("semantic-modal-mount-node")} open onClose={onClose} size="fullscreen">
      <Modal.Header>
        <h3>Assessments for {assessorQuery.data?.assessor?.profile?.fullName}</h3>
      </Modal.Header>
      <Modal.Content>
        <Tab
          menu={{ secondary: true, pointing: true }}
          activeIndex={assessmentRounds.findIndex(
            (round) => round.id === (selectedAssessmentRoundId || assessmentRounds[0]?.id),
          )}
          onTabChange={(_, data) => {
            const round = assessmentRounds[data.activeIndex || 0]
            setSelectedAssessmentRoundId(round?.id || null)
          }}
          panes={assessmentRounds.map((round) => {
            return {
              menuItem: round.name,
              render: () => {
                const hasConflictOfInterest = assessorQuery.data?.ideas?.some(
                  (idea) => idea.assessments?.length && idea.assessments[0].conflictOfInterest?.hasConflictOfInterest,
                )

                const doneIdeas = assessorQuery.data?.ideas
                  ?.map((idea) => {
                    const { _id, name, assessments = [] } = idea
                    const userAssessment = assessments.find((assessment) => {
                      return (
                        (assessment?.assessmentRoundId || "legacy") ===
                        (selectedAssessmentRoundId || assessmentRounds[0]?.id)
                      )
                    })
                    if (!userAssessment) {
                      return null
                    }

                    const absoluteScore = userAssessment?.assessment
                      ? Object.values(userAssessment.assessment)
                          .map((value) => value.score ?? 0)
                          .reduce((a, b) => a + b, 0)
                      : 0
                    const absoluteTotalScore = ideaTemplate.body
                      .flatMap((section) => {
                        if (
                          section.type === "assessment" &&
                          (section.assessmentRoundId || "legacy") ===
                            (selectedAssessmentRoundId || assessmentRounds[0]?.id)
                        ) {
                          return section.fields
                            .filter((field) => field.type === "assessment")
                            .map((field) => field?.options?.availableScore ?? 0)
                        }
                        return []
                      })
                      .reduce((a, b) => a + b, 0)

                    return {
                      _id,
                      name,
                      absoluteScore,
                      absoluteTotalScore,
                      userAssessment,
                      idea,
                    }
                  })
                  .filter(Boolean)

                return (
                  <ConfigurableTable
                    data={doneIdeas}
                    tableKey="assessorIdeas"
                    columns={[
                      {
                        key: "idea",
                        name: t("common:capitalise", { key: "generic.idea" }),
                        render: ({ item }) => (
                          <div style={{ display: "flex", alignItems: "center" }}>
                            <ImageWithFallback
                              style={{
                                width: 25,
                                height: 25,
                                objectFit: "cover",
                                borderRadius: 5,
                                marginRight: 5,
                              }}
                              src={util.ideaCoverImage(item, "200x200")}
                              fallbackSrc={util.ideaCoverImage()}
                              alt={`${t("common:capitalise", { key: "generic.idea" })} cover`}
                            />
                            <Link to={`/ideas/${item._id}`} style={{ textOverflow: "ellipsis", overflow: "hidden" }}>
                              {item.name}
                            </Link>
                          </div>
                        ),
                      },
                      {
                        name: "Status",
                        key: "status",
                        width: 100,
                        render: ({ item }) => {
                          return (
                            <>
                              {item?.userAssessment.startedAt || item?.userAssessment.isSubmitted ? (
                                item?.userAssessment.isSubmitted ? (
                                  <Label size="tiny" color="green">
                                    Submitted
                                  </Label>
                                ) : (
                                  <Label size="tiny" color="orange">
                                    In progress
                                  </Label>
                                )
                              ) : (
                                <Label size="tiny" color="red">
                                  Not started
                                </Label>
                              )}
                            </>
                          )
                        },
                      },
                      hasConflictOfInterest && {
                        key: "conflictOfInterest",
                        name: "Conflict of Interest",
                        width: 230,
                        render({ item }) {
                          return (
                            <div>
                              {item.userAssessment?.conflictOfInterest?.hasConflictOfInterest ? (
                                <div style={{ display: "flex" }}>
                                  <Popup
                                    trigger={
                                      <div>
                                        <Icon color="red" name="exclamation triangle" />
                                        <b>Conflict declared</b>
                                      </div>
                                    }
                                    disabled={!item?.userAssessment?.conflictOfInterest?.notes}
                                    content={
                                      <>
                                        <b>Notes:</b>
                                        <br />"{item?.userAssessment?.conflictOfInterest?.notes}"
                                      </>
                                    }
                                  />
                                </div>
                              ) : (
                                <i>-</i>
                              )}
                            </div>
                          )
                        },
                      },
                      {
                        name: "Total",
                        key: "total",
                        render: ({ item }) => {
                          return (
                            <>
                              {!item?.userAssessment?.conflictOfInterest?.hasConflictOfInterest ? (
                                <span>
                                  <strong>
                                    {item?.absoluteScore}/{item?.absoluteTotalScore}
                                  </strong>
                                </span>
                              ) : (
                                <i>-</i>
                              )}
                            </>
                          )
                        },
                      },
                      {
                        name: "Weighted %",
                        key: "weighted",
                        width: 100,
                        render: ({ item }) => {
                          return (
                            <>
                              {!item?.userAssessment?.conflictOfInterest?.hasConflictOfInterest ? (
                                <span>
                                  <strong>{Math.round(item?.userAssessment.score || 0)}%</strong>
                                </span>
                              ) : (
                                <i>-</i>
                              )}
                            </>
                          )
                        },
                      },
                      ...ideaTemplate.body
                        .filter(
                          (x) =>
                            x.type === "assessment" &&
                            (x.assessmentRoundId || "legacy") ===
                              (selectedAssessmentRoundId || assessmentRounds[0]?.id),
                        )
                        .flatMap((section) => {
                          return section.fields
                            .filter((field) => field.type !== "infotext")
                            .map((field) => ({
                              key: field.id,
                              name: field.title.substring(0, 30) + (field.title.length > 30 ? "..." : ""),
                              compactTitle: true,
                              width: 150,
                              render: ({ item }) => {
                                if (item?.userAssessment?.conflictOfInterest?.hasConflictOfInterest) {
                                  return <i>-</i>
                                }
                                const userAssessment = item.userAssessment.assessment[field.id]
                                const sectionAvailableScore = field.options?.availableScore

                                if (!userAssessment) {
                                  if (item?.userAssessment?.isSubmitted) {
                                    return (
                                      <i>
                                        This field was not part of the assessment at the time when this assessment was
                                        submitted. It is not considered in the final score.
                                      </i>
                                    )
                                  }
                                }

                                if (
                                  (userAssessment?.score && !!userAssessment.score) ||
                                  (userAssessment?.value && !!userAssessment.value)
                                ) {
                                  return (
                                    <Popup
                                      content={userAssessment?.notes}
                                      disabled={!userAssessment.notes}
                                      trigger={
                                        <p>
                                          {userAssessment.score
                                            ? `${userAssessment.score || 0}/${sectionAvailableScore}`
                                            : `${userAssessment.value}`}
                                        </p>
                                      }
                                    />
                                  )
                                }
                                if (userAssessment?.value && !!userAssessment.value) {
                                  return <>{userAssessment.value}</>
                                }
                              },
                            }))
                        }),
                    ]}
                    actions={(actionItems) =>
                      util.canManageChallenge(user, challenge) ||
                      util.hasPermission(user, "challenge.editAssessments", challenge?._id)
                        ? [
                            {
                              icon: "undo",
                              name: "Unsubmit assessment",
                              isVisible: !!actionItems[0].userAssessment.isSubmitted,
                              onClick: ([item]) =>
                                unsubmitAssessment(
                                  item._id,
                                  assessorId,
                                  selectedAssessmentRoundId || assessmentRounds[0]?.id,
                                ),
                            },
                            {
                              type: "Divider",
                              isVisible: !!actionItems[0].userAssessment.isSubmitted,
                            },
                            {
                              icon: "mail",
                              isVisible: !actionItems[0].userAssessment.isSubmitted,
                              disabled:
                                !!actionItems[0].userAssessment.lastContactedAt &&
                                moment().diff(moment(actionItems[0].userAssessment.lastContactedAt), "minutes") < 1,
                              name: (
                                <span style={{ display: "inline-flex", flexDirection: "column" }}>
                                  Remind assessor
                                  <span style={{ display: "block", color: "#888", fontSize: "0.9em" }}>
                                    Last contacted:{" "}
                                    {moment(
                                      actionItems[0]?.userAssessment?.lastContactedAt ||
                                        actionItems[0]?.userAssessment?.createdAt,
                                    ).fromNow()}
                                  </span>
                                </span>
                              ),
                              onClick: ([item]) =>
                                remindAssessor(assessorId, selectedAssessmentRoundId || assessmentRounds[0]?.id),
                            },
                            {
                              name: "Unassign from assessment",
                              checkPermission: "challenge.editAssessments",
                              checkPermissionScope: challenge._id,
                              onClick: ([item]) =>
                                unassignAssessor(
                                  item._id,
                                  assessorId,
                                  selectedAssessmentRoundId || assessmentRounds[0]?.id,
                                ),
                              icon: "remove user",
                            },
                          ]
                        : []
                    }
                  />
                )
              },
            }
          })}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </Modal>
  )
}

export default UserAssessmentsModal
